import {
  UsStreetAddress,
  postValidateAddress,
} from "src/components/address-validation/api";
import { NoomError } from "src/utils/error/NoomError";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { getFullState } from "src/utils/usStates";
import { createAppAsyncThunk } from "../../async-thunk";
import {
  suggestAddress,
  returnedInvalidAddress,
  setValidationErrorReasons,
} from "./action-creators";
import { selectResolvedPhysicalAddressShortRegion } from "./address-selectors";
import { setAddressValidated } from "./checkoutPhysicalAddress";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { ValidateAddressResponseProto_ValidationErrorReasonProto } from "@noom/noom-contracts/noom_contracts/billing/address/address_validation_service";
import { Reason } from "src/__generated_gql__/graphql";

function throwPhysicalAddressValidationFailedError() {
  const err = new NoomError(errorConstants.physicalAddressValidationFailed);
  (err as any).code = errorConstants.physicalAddressValidationFailed;
  throw err;
}

// Assumes United States
export const requestAddressValidation = createAppAsyncThunk(
  "checkoutPhysicalAddress/validate",
  async (_, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const address = selectResolvedPhysicalAddressShortRegion(getState());
    const request: UsStreetAddress = {
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: address.region,
      postalCode: address.zipcode,
      country: address.country,
    };
    try {
      const response = await postValidateAddress(request);
      trackBuyflowEvent("PhysicalAddressValidationAssessed", {
        validationStatus: response.status,
        errorReasons:
          response.errorReasons as ValidateAddressResponseProto_ValidationErrorReasonProto[],
        validationService: response.validationService,
      });

      // Handle different validation statuses
      if (response.status === "VALID") {
        dispatch(setAddressValidated(true));
        // Address is valid, nothing to do
        return;
      }

      if (response.status === "CORRECTED" && response.address) {
        // Address was corrected, suggest the corrected address
        const newAddress = {
          address1: response.address.addressLine1,
          city: response.address.city,
          region: getFullState(response.address.zoneCode as any),
          zipcode: response.address.postalCode,
        };
        dispatch(suggestAddress(newAddress));

        throwPhysicalAddressValidationFailedError();
      }

      const hasCustomHandledErrorReasons = response.errorReasons.some((er) =>
        [
          Reason.IncorrectSecondaryAddressNotRequired,
          Reason.IncorrectSecondaryAddressRequired,
          Reason.PoBox,
        ].includes(er.reason)
      );
      if (hasCustomHandledErrorReasons) {
        // Address has validation issues, store error reasons for modal display
        const errorReasons = response.errorReasons.map((er) => er.reason);
        dispatch(setValidationErrorReasons(errorReasons));

        throwPhysicalAddressValidationFailedError();
      }

      // Address is completely invalid, block user
      dispatch(returnedInvalidAddress());

      throwPhysicalAddressValidationFailedError();
    } catch (error) {
      // Handle network errors or other unexpected errors
      if (error instanceof NoomError) {
        throw error;
      }

      dispatch(returnedInvalidAddress());

      throwPhysicalAddressValidationFailedError();
    }
  }
);
